import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { X, MessageSquare, Zap } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = '/api';
const SESSION_KEY = 'exitIntentShown';

const typeColors = { general: '#64748B', question: '#6B8DE3', recommendation: '#5BAA6B', poll: '#C5962E', photo: '#9678CB', 'lost-found': '#D06060', traffic: '#C99430', alert: '#D06060', 'crime-safety': '#A04545', 'parenting-family': '#D08AA0', 'pets-animals': '#9C7BCC', 'food-drink': '#C97040', 'home-garden': '#5BAA6B', 'events-whats-on': '#9678CB', 'weather-travel': '#5A9FBD', 'business-shoutouts': '#C5962E', 'for-sale-free-wanted': '#4A9E7A', 'local-history': '#8B8178', 'rants-raves': '#D06060' };
const typeLabels = { general: 'General', question: 'Question', recommendation: 'Recommendation', poll: 'Poll', photo: 'Photo', 'lost-found': 'Lost & Found', traffic: 'Traffic', alert: 'Alert', 'crime-safety': 'Safety', 'parenting-family': 'Family', 'pets-animals': 'Pets', 'food-drink': 'Food & Drink', 'home-garden': 'Home & Garden', 'events-whats-on': 'Events', 'weather-travel': 'Weather', 'business-shoutouts': 'Shout-out', 'for-sale-free-wanted': 'For Sale', 'local-history': 'History', 'rants-raves': 'Rants & Raves' };

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const d = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(d / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const dy = Math.floor(h / 24);
  return `${dy}d ago`;
};

const ExitIntentPopup = () => {
  const [visible, setVisible] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [posts, setPosts] = useState([]);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'moderator') return;
    axios.get(`${API}/exit-intent-popup`).then(res => {
      setEnabled(res.data.enabled);
    }).catch(() => {});
    // Pre-fetch community posts
    axios.get(`${API}/community/posts?limit=3&sort=newest`).then(res => {
      const p = res.data?.posts || res.data || [];
      setPosts(p.slice(0, 3));
    }).catch(() => {});
  }, [user]);

  const handleMouseLeave = useCallback((e) => {
    if (e.clientY > 20) return;
    if (sessionStorage.getItem(SESSION_KEY)) return;
    sessionStorage.setItem(SESSION_KEY, 'true');
    setVisible(true);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    if (user?.role === 'admin' || user?.role === 'moderator') return;
    const timer = setTimeout(() => {
      document.addEventListener('mouseleave', handleMouseLeave);
    }, 4000);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [enabled, handleMouseLeave, user]);

  const handleJoinCommunity = () => {
    axios.post(`${API}/track-click`, {
      element: 'exit_intent_popup',
      headline: 'Community Pulse',
      subtext: 'Exit Intent',
      cta_text: 'Join the Conversation',
      cta_url: '/community'
    }).catch(() => {});
    navigate('/community');
    setVisible(false);
  };

  const handleClose = () => setVisible(false);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
      onClick={handleClose}
      data-testid="exit-intent-overlay"
    >
      <div
        className="relative bg-[#0F2C3E] rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden"
        onClick={e => e.stopPropagation()}
        data-testid="exit-intent-popup"
      >
        {/* Header */}
        <div className="px-5 pt-5 pb-3">
          <div className="flex items-center gap-2.5 mb-1">
            <div className="p-1.5 rounded-lg bg-[#D4A017]/15">
              <Zap className="h-4 w-4 text-[#D4A017]" />
            </div>
            <div>
              <p className="text-sm font-bold text-white">Community Pulse</p>
              <p className="text-[10px] text-white/40">What Essex is talking about right now</p>
            </div>
            <span className="ml-auto inline-flex items-center gap-1 text-[9px] text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 px-1.5 py-0.5 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />Live
            </span>
          </div>
        </div>

        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 p-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white/60 hover:text-white transition-colors"
          data-testid="exit-intent-close"
          aria-label="Close"
        >
          <X className="h-3.5 w-3.5" />
        </button>

        {/* Posts */}
        <div className="px-5 pb-3 space-y-2">
          {(posts.length > 0 ? posts : [
            { type: 'traffic', content: 'A12 delays near Chelmsford — anyone know what happened?', user_name: 'Sarah J.', created_at: null },
            { type: 'recommendation', content: 'Best fish & chips in Leigh-on-Sea? Go!', user_name: 'Mark T.', created_at: null },
            { type: 'lost-found', content: 'Missing tabby cat near Brentwood High Street', user_name: 'Emma K.', created_at: null },
          ]).map((post, i) => {
            const pType = post.type || post.post_type || 'general';
            const color = typeColors[pType] || '#64748B';
            return (
              <div key={post.id || i} className="bg-white/5 border border-white/10 rounded-xl p-2.5 hover:bg-white/10 transition-colors cursor-pointer" onClick={handleJoinCommunity}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: `${color}30`, color }}>{typeLabels[pType] || pType}</span>
                  {post.town && <span className="text-[9px] text-white/35">{post.town}</span>}
                  {post.created_at && <span className="ml-auto text-[9px] text-white/25">{timeAgo(post.created_at)}</span>}
                </div>
                <p className="text-[11px] text-white/75 leading-relaxed line-clamp-1">{new DOMParser().parseFromString((post.content || '').replace(/<[^>]*>/g, ''), 'text/html').body.textContent}</p>
                {post.display_name ? <p className="text-[9px] text-white/30 mt-1">by {post.display_name}</p> : post.user_name && <p className="text-[9px] text-white/30 mt-1">by {post.user_name}</p>}
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="px-5 pb-5 pt-1">
          <button
            onClick={handleJoinCommunity}
            className="w-full bg-[#D4A017] hover:bg-[#b8891a] text-[#0F2C3E] font-semibold py-2.5 px-6 rounded-xl transition-colors text-sm flex items-center justify-center gap-2"
            data-testid="exit-intent-join-btn"
          >
            <MessageSquare className="h-4 w-4" />
            Join the Conversation
          </button>
          <button
            onClick={handleClose}
            className="w-full mt-2 text-[10px] text-white/30 hover:text-white/50 transition-colors"
            data-testid="exit-intent-no-thanks"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExitIntentPopup;
